import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Typography,
  Stack,
  Fade,
} from "@mui/material";
import {
  CloudUpload,
  Delete,
  Close,
  PhotoCamera,
  Collections,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import useFetchDataWithPagination from "../../../utility/useFetchDataWithPagination";
import usePostData from "../../../utility/usePostData";

const PhotoGallery = () => {
  const { isLoading, error, submitData } = usePostData();
  const [images, setImages] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [previewImages, setPreviewImages] = useState([]);

  const { data, isLoading: loading, paginate, refreshSearch } =
    useFetchDataWithPagination("gallery");
  const [galleryList, setGalleryList] = useState(null);

  useEffect(() => {
    if (data?.data) {
      setGalleryList(data.data.data.data);
    //   console.log("Gallery Data:", data.data.data.data);
    }
  }, [data]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);

    const previews = files.map((file) => URL.createObjectURL(file));
    setPreviewImages(previews);
  };

  const removePreview = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    const newPreviews = previewImages.filter((_, i) => i !== index);
    setImages(newImages);
    setPreviewImages(newPreviews);
  };

  const deleteGalleryItem = (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette photo ?")) {
      submitData(`gallery/${id}`, null, "DELETE");
      refreshSearch();
    }
  };

  const saveData = (e) => {
    e.preventDefault();

    if (images.length === 0 || title === "") {
      setErrorMessage("Veuillez remplir tous les champs obligatoires");
      return;
    }

    const formData = new FormData();
    images.forEach((img) => {
      formData.append("images[]", img);
    });
    formData.append("title", title);
    formData.append("description", description);
    formData.append("category", category);

    submitData("/gallery", formData);
    refreshSearch();

    setImages([]);
    setTitle("");
    setDescription("");
    setCategory("");
    setPreviewImages([]);
    setErrorMessage("");
  };

  const filteredGallery = Array.isArray(galleryList)
    ? galleryList.filter(
        (item) => selectedFilter === "all" || item.category === selectedFilter
      )
    : [];

  const categories = Array.isArray(galleryList)
    ? [...new Set(galleryList.map((item) => item.category).filter(Boolean))]
    : [];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {isLoading && <LinearProgress color="success" sx={{ mb: 2 }} />}

      <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
          <PhotoCamera color="success" />
          <Typography variant="h5" fontWeight={600} color="success.main">
            Ajouter des photos à la galerie
          </Typography>
        </Stack>

        {(errorMessage || error) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage || error?.message}
          </Alert>
        )}

        <Box component="form" onSubmit={saveData}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Titre"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                variant="outlined"
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Catégorie"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                variant="outlined"
                placeholder="Ex: Événements, Nature, Portraits"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                component="label"
                variant="outlined"
                fullWidth
                startIcon={<CloudUpload />}
                sx={{ height: 56 }}
              >
                {images.length > 0
                  ? `${images.length} image(s) sélectionnée(s)`
                  : "Choisir des images"}
                <input
                  type="file"
                  hidden
                  accept=".jpeg,.jpg,.png"
                  multiple
                  onChange={handleImageChange}
                />
              </Button>
            </Grid>
          </Grid>

          {previewImages.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Aperçu ({previewImages.length} image{previewImages.length > 1 ? "s" : ""})
              </Typography>
              <Grid container spacing={2}>
                {previewImages.map((preview, index) => (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                    <Box sx={{ position: "relative" }}>
                      <Box
                        component="img"
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        sx={{
                          width: "100%",
                          height: 120,
                          objectFit: "cover",
                          borderRadius: 1,
                          border: "2px solid #ddd",
                        }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removePreview(index)}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          bgcolor: "rgba(255,255,255,0.9)",
                          "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            startIcon={<CloudUpload />}
            sx={{
              mt: 3,
              bgcolor: "success.main",
              "&:hover": { bgcolor: "success.dark" },
            }}
          >
            Télécharger ({images.length} image{images.length > 1 ? "s" : ""})
          </Button>
        </Box>
      </Paper>

      <Box>
        {loading && <LinearProgress color="error" sx={{ mb: 2 }} />}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Collections color="success" />
            <Typography variant="h5" fontWeight={600}>
              Galerie photos
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label="Tout"
              color={selectedFilter === "all" ? "success" : "default"}
              onClick={() => setSelectedFilter("all")}
              sx={{ cursor: "pointer" }}
            />
            {categories.map((cat, idx) => (
              <Chip
                key={idx}
                label={cat}
                color={selectedFilter === cat ? "success" : "default"}
                onClick={() => setSelectedFilter(cat)}
                sx={{ cursor: "pointer" }}
              />
            ))}
          </Stack>
        </Stack>

        <Grid container spacing={3}>
          {filteredGallery &&
            filteredGallery.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Fade in timeout={300}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="220"
                      image={item.image}
                      alt={item.title}
                      sx={{ objectFit: "cover" }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {item.title}
                      </Typography>
                      {item.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          {item.description}
                        </Typography>
                      )}
                      {item.category && (
                        <Chip
                          label={item.category}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </CardContent>
                    <CardActions>
                      <Button
                        fullWidth
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<Delete />}
                        onClick={() => deleteGalleryItem(item.id)}
                      >
                        Supprimer
                      </Button>
                    </CardActions>
                  </Card>
                </Fade>
              </Grid>
            ))}
        </Grid>

        {(!filteredGallery || filteredGallery.length === 0) && (
          <Paper
            elevation={0}
            sx={{
              textAlign: "center",
              py: 8,
              bgcolor: "grey.50",
              borderRadius: 2,
            }}
          >
            <Collections sx={{ fontSize: 80, color: "grey.300", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Aucune photo dans la galerie
            </Typography>
          </Paper>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          {paginate()}
        </Box>
      </Box>
    </Container>
  );
};

export default PhotoGallery;